import { Box } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { PaloModal } from '@synergy/components-palo';
import { PoolButton } from '@synergy/components-next';
import { ReactElement } from 'react';
import { tokens } from '@synergy/theme-palo';

export interface PaloSupplierChangeDialogProps {
    open: boolean;
    /** How many positions the document already carries. */
    count: number;
    /** The supplier the positions were picked from. */
    previousSupplier?: string;
    onKeep: () => void;
    onRemove: () => void;
    onCancel: () => void;
}

/**
 * Asked when the supplier changes and positions are already on the document.
 *
 * Those positions were picked from the supplier the document had until now,
 * with that supplier's prices; the new supplier rarely carries the same
 * articles. Leaving it silent is how a document ends up mixing two suppliers
 * (Prüfbericht Roland Niedermayr, 28.09.2026, Punkt 5).
 */
export const PaloSupplierChangeDialog = ({
    open,
    count,
    previousSupplier,
    onKeep,
    onRemove,
    onCancel,
}: PaloSupplierChangeDialogProps): ReactElement => (
    <PaloModal
        isVisible={open}
        title={<FormattedMessage id="app.positions.supplierChange.title" defaultMessage="Lieferant wechseln" />}
        handleClose={onCancel}
        width="sm"
        themeContent={true}
        actionChildren={
            <>
                <PoolButton variant="text" onClick={onCancel}>
                    <FormattedMessage id="app.common.cancel" />
                </PoolButton>
                <PoolButton variant="outlined" color="error" onClick={onRemove}>
                    <FormattedMessage id="app.positions.supplierChange.remove" defaultMessage="Positionen entfernen" />
                </PoolButton>
                <PoolButton onClick={onKeep}>
                    <FormattedMessage id="app.positions.supplierChange.keep" defaultMessage="Positionen behalten" />
                </PoolButton>
            </>
        }
    >
        <Box sx={{ fontSize: '13px', color: tokens.color.ink }}>
            <FormattedMessage
                id="app.positions.supplierChange.text"
                defaultMessage="{count, plural, one {Eine Position stammt} other {# Positionen stammen}} von {supplier}. Beim Wechsel des Lieferanten bleiben sie mit den Artikeln und Preisen dieses Lieferanten stehen."
                values={{ count, supplier: previousSupplier ?? '—' }}
            />
        </Box>
    </PaloModal>
);
